import { Component, OnInit, ViewChild } from '@angular/core';
import { routes } from '@app/consts';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import moment from 'moment';
import { from } from 'rxjs';
import { BuySellService } from '@app/modules/time-off/services/buy-sell.service';

@Component({
  selector: 'app-timeoff-buy-sell-hours-report',
  templateUrl: './timeoff-buy-sell-hours-report.component.html',
  styleUrls: ['./timeoff-buy-sell-hours-report.component.scss']
})
export class TimeoffBuySellHoursReportComponent implements OnInit {
  @ViewChild('excelexport') excelexport: any;
  public grid: GridComponent;
  public routes: typeof routes = routes;
  public bindingType = 'array';
  public gridDataResult: GridDataResult;
  excelData: any;
  gridId: string = 'TimeOffBuySellHoursDataGrid';
  isLoading: boolean;
  pageSize: number = 20;
  skip: number = 0;

  sortString: string;
  filterString: string;

  excelColumns: any[] = [
    { field: 'employee', title: this.translate.instant('BuySell-Employee') },
    { field: 'customClientId', title: this.translate.instant('BuySell-CustomClientId') },
    { field: 'userId', title: this.translate.instant('BuySell-UserId') },
    { field: 'payrollNumber', title: this.translate.instant('BuySell-PayrollNumber') },
    { field: 'hoursPerWeek', title: this.translate.instant('BuySell-HoursPerWeek') },
    { field: 'plan', title: this.translate.instant('BuySell-Plan') },
    { field: 'type', title: this.translate.instant('BuySell-Type') },
    { field: 'hours', title: this.translate.instant('BuySell-Hours') },
    { field: 'hourlyCost', title: this.translate.instant('BuySell-HourlyCost') },
    { field: 'totalCost', title: this.translate.instant('BuySell-TotalCost') },
    { field: 'totalCostPerQuarter', title: this.translate.instant('BuySell-TotalCostPerQuarter') },
    { field: 'totalCostPerMonth', title: this.translate.instant('BuySell-TotalCostPerMonth') },
    { field: 'totalCostPerWeek', title: this.translate.instant('BuySell-TotalCostPerWeek') },
    { field: 'totalCostPerDay', title: this.translate.instant('BuySell-TotalCostPerDay') },
    { field: 'status', title: this.translate.instant('BuySell-Status') },
  ];

  public columns: any[] = [
    { field: 'employee', title: this.translate.instant('BuySell-Employee'), localizationCode: 'BuySell-Employee', type: 'employee' },
    { field: 'customClientId', title: this.translate.instant('BuySell-CustomClientId'), localizationCode: 'BuySell-CustomClientId' },
    { field: 'userId', title: this.translate.instant('BuySell-UserId'), localizationCode: 'BuySell-UserId' },
    { field: 'payrollNumber', title: this.translate.instant('BuySell-PayrollNumber'), localizationCode: 'BuySell-PayrollNumber' },
    { field: 'hoursPerWeek', title: this.translate.instant('BuySell-HoursPerWeek'), dataType: 'String', localizationCode: 'BuySell-HoursPerWeek' },
    { field: 'plan', subField: 'name', title: this.translate.instant('BuySell-Plan'), localizationCode: 'BuySell-Plan' },
    { field: "type", subField: "name", title: this.translate.instant('BuySell-Type'), localizationCode: 'BuySell-Type', type: 'chip', chipValue: ['', '', 'Buy', 'Sell'] },
    { field: 'hours', title: this.translate.instant('BuySell-Hours'), dataType: 'String', localizationCode: 'BuySell-Hours' },
    { field: 'hourlyCost', title: this.translate.instant('BuySell-HourlyCost'), dataType: 'String', localizationCode: 'BuySell-HourlyCost' },
    { field: 'totalCost', title: this.translate.instant('BuySell-TotalCost'), dataType: 'String', localizationCode: 'BuySell-TotalCost' },
    { field: 'totalCostPerQuarter', title: this.translate.instant('BuySell-TotalCostPerQuarter'), dataType: 'String', localizationCode: 'BuySell-TotalCostPerQuarter' },
    { field: 'totalCostPerMonth', title: this.translate.instant('BuySell-TotalCostPerMonth'), dataType: 'String', localizationCode: 'BuySell-TotalCostPerMonth' },
    { field: 'totalCostPerWeek', title: this.translate.instant('BuySell-TotalCostPerWeek'), dataType: 'String', localizationCode: 'BuySell-TotalCostPerWeek' },
    { field: 'totalCostPerDay', title: this.translate.instant('BuySell-TotalCostPerDay'), dataType: 'String', localizationCode: 'BuySell-TotalCostPerDay' },
    { field: "status", subField: "name", title: this.translate.instant('BuySell-Status'), localizationCode: 'BuySell-Status', type: 'chip', chipValue: ['Approved', 'Denied', 'Pending'] },
  ];

  sortableColumns: any[] = [
    { field: 'employee', sortValue: ['employee.firstName', 'employee.lastName'] },
    { field: 'customClientId', sortValue: 'employee.customClientId' },
    { field: 'userId', sortValue: 'employee.id' },
    { field: 'payrollNumber', sortValue: 'employee.payrollNumber' },
    { field: 'hoursPerWeek', sortValue: 'employee.hoursPerWeek' },
    { field: 'plan', sortValue: 'plan.name' },
    { field: 'type', sortValue: 'type.name' },
    { field: 'hours', sortValue: 'hours' },
    { field: 'hourlyCost', sortValue: 'hourlyCost' },
    { field: 'status', sortValue: 'status.name' },
  ];

  filterCategories: any[] = [
    { field: "employee.firstname", title: this.translate.instant('EmployeeFirstname'), type: "String", dataType: 'String' },
    { field: "employee.lastname", title: this.translate.instant('EmployeeLastname'), type: "String", dataType: 'String' },
    { field: "employee.customClientId", title: this.translate.instant('BuySell-CustomClientId'), type: "String", dataType: 'String' },
    { field: "employee.id", title: this.translate.instant('BuySell-UserId'), type: "String", dataType: 'String' },
    { field: "employee.payrollNumber", title: this.translate.instant('BuySell-PayrollNumber'), type: "String", dataType: 'String' },
    { field: "employee.hoursPerWeek", title: this.translate.instant('BuySell-HoursPerWeek'), type: "String", dataType: 'String' },
    { field: "plan.name", title: this.translate.instant('BuySell-Plan'), type: "String", dataType: 'String' },
    { field: "type.name", title: this.translate.instant('BuySell-Type'), type: "String", dataType: 'String' },
    { field: "hours", title: this.translate.instant('BuySell-Hours'), type: "Number", dataType: 'Number' },
    { field: "status.name", title: this.translate.instant('BuySell-Status'), type: "String", dataType: 'String' },
  ];

  searchValue: any;
  searchFilterString: string;
  getRequest: any;
  error: boolean;
  showAsOfDateField: boolean = true;
  asOfDate: any = moment().format('YYYY-MM-DD');
  fileName: string = this.translate.instant('TimeOffBuySellHoursReport') + '_' + moment().format('YYYY-MM-DD') + '.xlsx';
  baseFilter = `(published = "true")`;

  constructor(
    private translate: TranslateService,
    private overlayService: OverlayService,
    private buySellService: BuySellService
  ) {
    this.getData();

    // Bind 'this' explicitly to capture the execution context of the component.
    this.allData = this.allData.bind(this);
  }

  ngOnInit(): void {
  }

  public allData() {
    this.overlayService.show('Loading');

    let filter = this.baseFilter;
    if (this.filterString) {
      filter += ` AND (${this.filterString})`;
    }

    from(this.buySellService.getAllBuySellHoursReportData(filter, this.sortString, this.asOfDate))
      .subscribe({
        next: (res) => {
          this.excelData = res.data;

          this.excelData.map((item) => {
            item.plan = item.plan?.name;
            item.status = item.status?.name;
            item.customClientId = item.employee?.customClientId;
            item.userId = item.employee?.id;
            item.payrollNumber = item.employee?.payrollNumber;
            item.hoursPerWeek = item.employee?.hoursPerWeek;
            item.employee = `${item.employee.firstName} ${item.employee.lastName}`;
            item.totalCost = item.type?.id === 'BUY' ? -(item.hourlyCost * item.hours) : item.hourlyCost * item.hours;
            item.totalCostPerDay = parseFloat((item.type?.id === 'BUY' ? -(item.hourlyCost * item.hours / 365) : item.hourlyCost * item.hours / 365).toFixed(2));
            item.totalCostPerWeek = parseFloat((item.type?.id === 'BUY' ? -((item.hourlyCost * item.hours) / 52) : (item.hourlyCost * item.hours) / 52).toFixed(2));
            item.totalCostPerMonth = parseFloat((item.type?.id === 'BUY' ? -((item.hourlyCost * item.hours) / 12) : (item.hourlyCost * item.hours) / 12).toFixed(2));
            item.totalCostPerQuarter = parseFloat((item.type?.id === 'BUY' ? -((item.hourlyCost * item.hours) / 4) : (item.hourlyCost * item.hours) / 4).toFixed(2));
            item.type = item.type?.name;
          });

          // if I dont have this timeout first time I download the report the data will be blank
          setTimeout(() => {
            this.excelexport.save();
          }, 1000);
        },
        error: (e) => {
          this.overlayService.hide();
        },
        complete: () => this.overlayService.hide()
      });
  }

  getData(): void {
    this.isLoading = true;

    let filter = this.baseFilter;
    if (this.filterString) {
      filter += ` AND (${this.filterString})`;
    }

    this.getRequest = this.buySellService.getBuySellHoursReportData(this.pageSize, this.skip, filter, this.sortString, this.asOfDate)
      .subscribe(
        {
          next: (v) => {
            v.data.map((item) => {
              item.customClientId = item.employee?.customClientId;
              item.userId = item.employee?.id;
              item.payrollNumber = item.employee?.payrollNumber;
              item.hoursPerWeek = item.employee?.hoursPerWeek;
              item.totalCost = item.type?.id === 'BUY' ? -(item.hourlyCost * item.hours) : item.hourlyCost * item.hours;
              item.totalCostPerDay = parseFloat((item.type?.id === 'BUY' ? -(item.hourlyCost * item.hours / 365) : item.hourlyCost * item.hours / 365).toFixed(2));
              item.totalCostPerWeek = parseFloat((item.type?.id === 'BUY' ? -((item.hourlyCost * item.hours) / 52) : (item.hourlyCost * item.hours) / 52).toFixed(2));
              item.totalCostPerMonth = parseFloat((item.type?.id === 'BUY' ? -((item.hourlyCost * item.hours) / 12) : (item.hourlyCost * item.hours) / 12).toFixed(2));
              item.totalCostPerQuarter = parseFloat((item.type?.id === 'BUY' ? -((item.hourlyCost * item.hours) / 4) : (item.hourlyCost * item.hours) / 4).toFixed(2));
            });

            this.gridDataResult = {
              data: v.data,
              total: v.total,
            }
          },
          error: (e) => {
            this.error = true;
            this.isLoading = false;
          },
          complete: () => this.isLoading = false
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }
    if (this.asOfDate !== gridState.asOfDate) {
      this.asOfDate = gridState.asOfDate;
    }

    this.getRequest.unsubscribe();
    this.getData();
  }
}
